import PropTypes from 'prop-types';
import {useEffect, useRef, useState} from 'react';
import {Form, Popup} from 'semantic-ui-react';

import styles from './AuthSearch.module.scss';

export default function AuthSearch({
  itemKeyName,
  name,
  value,
  onChange,
  onSearch,
  itemRenderer,
  error,
  disabled,
  ...rest
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldSendRequests, setShouldSendRequests] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  useEffect(() => {
    // make sure a new AbortController is created on every rerender (cancelling once will cancel all consecutive requests)
    const abortController = new AbortController();

    (async () => {
      if (!shouldSendRequests) {
        return;
      }

      if (!searchQuery) {
        setResults([]);
      } else {
        setIsLoading(true);

        try {
          const uniqueIdentifiers = new Set();
          const results = [];
          const responses = await onSearch(searchQuery, abortController.signal);

          for (const {data} of responses) {
            for (const item of data) {
              if (uniqueIdentifiers.has(item[itemKeyName])) {
                continue;
              }

              results.push(item);
              uniqueIdentifiers.add(item[itemKeyName]);
            }
          }

          setResults(results);
        } catch {
          setResults([]);
        } finally {
          setIsLoading(false);
        }
      }

      setShouldSendRequests(false);
    })();

    return () => {
      abortController.abort(
        'Requests to get auth resources have been cancelled due to the component being rerendered!'
      );
    };
  }, [shouldSendRequests, searchQuery, onSearch, itemKeyName]);

  useEffect(() => {
    // needed to properly initialize the field
    if (value) {
      setResults([{[itemKeyName]: value}]);
    }
  }, [value, itemKeyName]);

  return (
    <Popup
      trigger={
        <Form.Dropdown
          {...rest}
          className={styles.AuthSearch}
          searchInput={{name, 'aria-label': 'Search'}}
          disabled={disabled}
          onBlur={() => {
            if (disabled) {
              return;
            }

            if (!value) {
              setResults([]);
            } else {
              // revert to the current value
              setResults([{[itemKeyName]: value}]);
            }
          }}
          onChange={(_evt, {value}) => {
            if (disabled) {
              return;
            }

            if (value) {
              onChange(results.find(element => element[itemKeyName] === value));
            } else {
              onChange({});
              setSearchQuery('');
              setResults([]);
            }
          }}
          onSearchChange={({target: {value}}) => {
            if (disabled) {
              return;
            }

            setSearchQuery(value);

            if (timer.current !== null) {
              clearTimeout(timer.current);
            }

            timer.current = setTimeout(() => {
              setShouldSendRequests(true);
            }, 600);
          }}
          search={items => items}
          value={value}
          options={results.map(itemRenderer)}
          allowAdditions={false}
          loading={isLoading}
          error={!!error}
          openOnFocus={false}
          selectOnBlur={false}
          icon="search"
          fluid
          selection
        />
      }
      content={error}
      position="right center"
      open={!!error}
    />
  );
}

AuthSearch.propTypes = {
  itemKeyName: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  itemRenderer: PropTypes.func.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool,
};

AuthSearch.defaultProps = {
  disabled: false,
};
